import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, Loader2 } from 'lucide-react';
import { InfoCircle, Xmark } from 'iconoir-react';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogTitle } from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { cn } from 'lib/utils';

const BOTFATHER_URL = 'https://t.me/BotFather';

type TelegramBotConfigModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnect?: (botToken: string) => Promise<void>;
  onCancel?: () => void;
  isConnecting?: boolean;
};

type SetupStep = {
  id: string;
  content: ReactNode;
};

export const TelegramBotConfigModal = ({
  open,
  onOpenChange,
  onConnect,
  onCancel,
  isConnecting = false,
}: TelegramBotConfigModalProps) => {
  const { t } = useTranslation();
  const [botToken, setBotToken] = useState('');

  useEffect(() => {
    if (!open) {
      setBotToken('');
    }
  }, [open]);

  const emphasisClass = 'font-semibold text-grey-700 dark:text-true-white';

  const steps: SetupStep[] = [
    {
      id: 'open-botfather',
      content: (
        <>
          {t('TELEGRAM_SETUP_STEP_1', {
            defaultValue: 'Open Telegram and search for',
          })}{' '}
          <span className={emphasisClass}>@BotFather</span>.
        </>
      ),
    },
    {
      id: 'newbot',
      content: (
        <>
          {t('TELEGRAM_SETUP_STEP_2_PREFIX', { defaultValue: 'Send the command' })}{' '}
          <span className={emphasisClass}>/newbot</span>{' '}
          {t('TELEGRAM_SETUP_STEP_2_SUFFIX', { defaultValue: 'to create a bot.' })}
        </>
      ),
    },
    {
      id: 'username',
      content: (
        <>
          {t('TELEGRAM_SETUP_STEP_3_PREFIX', {
            defaultValue: 'Give your bot a username that ends with',
          })}{' '}
          <span className={emphasisClass}>_bot</span>.
        </>
      ),
    },
    {
      id: 'paste-token',
      content: (
        <>
          {t('TELEGRAM_SETUP_STEP_4_PREFIX', {
            defaultValue:
              'When BotFather returns the bot token, copy it and paste it into the',
          })}{' '}
          <span className={emphasisClass}>
            {t('BOTFATHER_TOKEN', { defaultValue: 'BotFather token' })}
          </span>{' '}
          {t('TELEGRAM_SETUP_STEP_4_SUFFIX', { defaultValue: 'field below.' })}
        </>
      ),
    },
    {
      id: 'connect',
      content: (
        <>
          {t('TELEGRAM_SETUP_STEP_5_PREFIX', { defaultValue: 'Click' })}{' '}
          <span className={emphasisClass}>{t('CONNECT', { defaultValue: 'Connect' })}</span>{' '}
          {t('TELEGRAM_SETUP_STEP_5_SUFFIX', {
            defaultValue: 'to link your Telegram bot with this agent.',
          })}
        </>
      ),
    },
  ];

  const handleClose = () => {
    if (isConnecting) return;
    onOpenChange(false);
    onCancel?.();
  };

  const handleConnect = async () => {
    const token = botToken.trim();
    if (!token || !onConnect) return;

    try {
      await onConnect(token);
      onOpenChange(false);
    } catch {
      // Keep the modal open so the user can retry after an error toast.
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && handleClose()}>
      <DialogContent
        hideClose
        className="flex max-h-[90vh] w-full max-w-[565px] flex-col gap-6 overflow-y-auto rounded-xl border-0 bg-white p-4 shadow-[0_24px_80px_rgba(15,23,42,0.18)] dark:bg-background sm:p-8"
      >
        <button
          type="button"
          aria-label={t('CLOSE') || 'Close'}
          onClick={handleClose}
          className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center text-[#6D6D6D] focus:outline-none dark:text-true-white"
        >
          <Xmark width={14} height={14} />
        </button>

        <DialogTitle className="self-stretch break-words font-sans text-[24px] font-semibold not-italic leading-[120%] tracking-normal text-grey-700 dark:text-true-white">
          {t('INTEGRATE_TELEGRAM', { defaultValue: 'Integrate Telegram' })}
        </DialogTitle>

        <div className="flex w-full items-start gap-2 rounded bg-[#FFFAEB] p-4">
          <div className="flex shrink-0 items-start px-px pb-1 pt-px">
            <InfoCircle
              width={13.333}
              height={13.333}
              color="#725111"
              strokeWidth={1.25}
              className="shrink-0"
            />
          </div>
          <div className="flex flex-col gap-2">
            <p className="self-stretch break-words font-sans text-[12px] font-semibold not-italic leading-[120%] text-[#725111]">
              {t('BOT_CONFIGURATION_QUICK_START', {
                defaultValue: 'Bot Configuration — Quick Start',
              })}
            </p>
            <p className="self-stretch break-words font-sans text-[12px] font-normal not-italic leading-[120%] text-[#725111]">
              {t('TELEGRAM_CONNECT_DESCRIPTION', {
                defaultValue:
                  'Create a bot with BotFather, paste the token below, then connect your Telegram bot.',
              })}
            </p>
          </div>
        </div>

        <div className="flex w-full flex-col items-start gap-8 self-stretch pb-4">
          <ol className="flex w-full flex-col gap-3 self-stretch">
            {steps.map((step, index) => (
              <li key={step.id} className="flex items-start gap-3">
                <span
                  className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-black text-[12px] font-semibold leading-none text-white dark:bg-foreground dark:text-background"
                  aria-hidden
                >
                  {index + 1}
                </span>
                <p className="font-sans text-sm font-normal not-italic leading-[160%] text-grey-700 dark:text-grey-300">
                  {step.content}
                </p>
              </li>
            ))}
          </ol>

          <Button
            type="button"
            variant="outline"
            className="h-11 gap-1.5 rounded-[62px] border border-[#D9DBE9] px-5 text-base font-medium leading-[115%] text-[#170F49] shadow-none hover:opacity-90"
            style={{
              backgroundImage: 'linear-gradient(151deg, #FFF -62.65%, #FBFBFE 83.01%)',
            }}
            onClick={() => window.open(BOTFATHER_URL, '_blank', 'noopener,noreferrer')}
          >
            <Bot className="size-4" aria-hidden />
            {t('OPEN_BOTFATHER', { defaultValue: 'Open BotFather' })}
          </Button>

          <div className="flex w-full flex-col items-start gap-1 self-stretch">
            <label
              htmlFor="botfather-token"
              className="font-sans text-sm font-medium not-italic leading-[160%] text-grey-700 dark:text-true-white"
            >
              {t('BOTFATHER_TOKEN', { defaultValue: 'BotFather token' })}
            </label>
            <Input
              id="botfather-token"
              value={botToken}
              onChange={(event) => setBotToken(event.target.value)}
              placeholder={t('PASTE_BOT_TOKEN_HERE', {
                defaultValue: 'Paste bot token here',
              })}
              autoComplete="off"
              spellCheck={false}
              className="flex !h-12 w-full items-center gap-4 self-stretch rounded-lg border border-grey-100 bg-white px-5 py-4 font-sans text-sm font-normal not-italic leading-[120%] text-grey-700 shadow-none placeholder:font-normal placeholder:text-grey-300 focus-visible:ring-1 focus-visible:ring-ring dark:border-border dark:bg-transparent dark:text-true-white dark:placeholder:text-grey-400"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-4 self-stretch border-t border-grey-100 pt-6 dark:border-border">
          <Button
            type="button"
            variant="outline"
            className="h-11 w-[101px] gap-1.5 rounded-[62px] border border-[#D9DBE9] px-0 text-base font-medium leading-[115%] text-[#170F49] shadow-none hover:opacity-90"
            style={{
              backgroundImage: 'linear-gradient(151deg, #FFF -62.65%, #FBFBFE 83.01%)',
            }}
            disabled={isConnecting}
            onClick={handleClose}
          >
            {t('CANCEL') || 'Cancel'}
          </Button>
          <Button
            type="button"
            className={cn(
              'h-11 min-w-[120px] gap-1.5 rounded-[62px] border-0 px-6 text-base font-semibold leading-[115%] text-white shadow-none',
              '!bg-black-base hover:!bg-grey-900',
              'disabled:!bg-grey-200 disabled:!text-grey-500 disabled:!opacity-100',
              'dark:!bg-[#3D3D3D] dark:hover:!bg-[#4F4F4F] dark:text-true-white',
              'disabled:dark:!bg-[#27272C] disabled:dark:!text-grey-500 disabled:dark:!opacity-100'
            )}
            disabled={!botToken.trim() || isConnecting}
            onClick={handleConnect}
          >
            {isConnecting ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : null}
            {t('CONNECT') || 'Connect'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
